/**
 * EarningsTab.jsx — earnings/EarningsTab.jsx
 *
 * The "Earnings This Week" inner tab.
 *
 * Data flow:
 *   1. User clicks ⟳ Sync Earnings
 *   2. Worker fetches real Yahoo Finance earnings calendar (/api/earnings-calendar)
 *      Returns actual tickers reporting this week with dates, market caps, volumes
 *   3. Ticker list is injected into the Haiku prompt (SYSTEM_EARNINGS in worker/index.js)
 *   4. Haiku enriches each stock with: implied move, IV rank, historical move,
 *      EPS estimate, directional probability, predicted range, bull/bear thesis
 *   5. Result is cached in localStorage for 4 hours
 *
 * If Yahoo calendar is unavailable, falls back to asking Haiku to recall
 * upcoming earnings from training knowledge (less reliable).
 *
 * Sort options: volatility score, implied move, IV rank, bull probability
 * Filter: by sector
 */

import { useState, useCallback, useEffect } from 'react';
import { CacheBar, EmptyState, Spinner, ErrMsg, fmt,
         callAIEndpoint, fetchEarningsCalendar, loadLocalCache, saveLocalCache } from './EarningsShared';
import EarningsCard from './EarningsCard';

export default function EarningsTab({ t }) {
  // KV cache is shared — load from Worker, not localStorage
  const [data,    setData]    = useState(null);
  const [cacheTs, setTs]      = useState(null);
  const [stale,   setStale]   = useState(true);
  const [sharedCache, setSharedCache] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error,   setError]   = useState(null);
  const [expanded,setExpanded]= useState(0);
  const [sortBy,  setSortBy]  = useState('volatilityScore');
  const [sector,  setSector]  = useState('All');

  // Load from KV cache on mount
  useEffect(() => {
    (async () => {
      setSyncing(true);
      try {
        const calendar = await fetchEarningsCalendar();
        const tickers  = calendar?.tickers || [];
        const weekStart = calendar?.weekStart || '';
        const weekEnd   = calendar?.weekEnd   || '';
        const weekRange = calendar?.weekRange || 'this week';
        const today     = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        let userMsg = buildEarningsPrompt(today, weekStart, weekEnd, weekRange, tickers);
        // Check KV cache first (force=false)
        const result = await callAIEndpoint('ai/earnings', userMsg, false);
        setData(result);
        setTs(result.fetchedAt || Date.now());
        setStale(false);
        setSharedCache(result.sharedCache ?? false);
        saveLocalCache('od:earnings', result); // backup
      } catch(e) {
        // Fall back to localStorage if Worker unavailable
        const local = loadLocalCache('od:earnings');
        if (local?.data) { setData(local.data); setTs(local.ts); setStale(true); }
        else setError(e.message);
      } finally { setSyncing(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sync = useCallback(async (force = true) => {
    setSyncing(true); setError(null);
    try {
      const calendar  = await fetchEarningsCalendar();
      const tickers   = calendar?.tickers || [];
      const weekStart = calendar?.weekStart || '';
      const weekEnd   = calendar?.weekEnd   || '';
      const weekRange = calendar?.weekRange || 'this week';
      const today     = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      const userMsg   = buildEarningsPrompt(today, weekStart, weekEnd, weekRange, tickers);
      const result    = await callAIEndpoint('ai/earnings', userMsg, force);
      setData(result);
      setTs(result.fetchedAt || Date.now());
      setStale(false);
      setSharedCache(result.sharedCache ?? false);
      saveLocalCache('od:earnings', result);
      setExpanded(0);
    } catch(e) { setError(e.message); }
    finally { setSyncing(false); }
  }, []);

  function buildEarningsPrompt(today, weekStart, weekEnd, weekRange, tickers) {
    if (tickers.length > 0) {
      const tickerList = tickers.slice(0, 50).map(tk => {
        const mcap = tk.marketCap ? `${(tk.marketCap / 1e9).toFixed(0)}B` : 'unknown';
        const vol  = tk.avgVolume ? `${(tk.avgVolume / 1e6).toFixed(1)}M` : 'unknown';
        return `${tk.ticker} (${tk.company}, reportDate: ${tk.reportDate}, reportTime: ${tk.reportTime}, mktcap: ~${mcap}, avgvol: ~${vol}/day, sector: ${tk.sector})`;
      }).join('\n');
      return `Today is ${today}. Earnings window: ${weekStart} to ${weekEnd} (this week and next week).\n\nThe COMPLETE verified list of companies reporting earnings per Yahoo Finance:\n\n${tickerList}\n\nCRITICAL RULES - VIOLATIONS ARE SERIOUS ERRORS:\n- You MUST ONLY use tickers from the verified list above. NEVER add companies not listed.\n- TSLA, NFLX, AMZN, GOOGL are common hallucinations - DO NOT include them unless they appear in the list above with a verified reportDate in this window.\n- Do NOT use your training knowledge to add earnings dates - ONLY use the reportDate values from the list.\n- Include the exact reportDate from the verified list in each earnings entry.\n- Enrich each qualifying liquid large-cap stock from the list with volatility and options analysis.`;
    }
    return `Today is ${today}. The earnings calendar for ${weekRange} could not be retrieved from Yahoo Finance.\n\nBased on your training knowledge, provide earnings analysis for 6-8 major liquid US stocks likely reporting in this window. Focus on large-cap technology, consumer, and financial names with active options markets.\n\nIMPORTANT: You MUST return a non-empty earnings array. Include your best estimate of reportDate (YYYY-MM-DD). Do not return an empty list.`;
  }

  // Derived data for rendering
  const sectors  = data ? ['All', ...Array.from(new Set((data.earnings || []).map(s => s.sector).filter(Boolean)))] : ['All'];
  const filtered = data?.earnings
    ? [...data.earnings]
        .filter(s => sector === 'All' || s.sector === sector)
        .sort((a, b) => (b[sortBy] ?? 0) - (a[sortBy] ?? 0))
    : [];

  // Summary stats
  const avgImpl = filtered.length ? filtered.reduce((s, d) => s + (d.impliedMove || 0), 0) / filtered.length : 0;
  const avgIV   = filtered.length ? filtered.reduce((s, d) => s + (d.ivRank     || 0), 0) / filtered.length : 0;
  const hiVol   = filtered.filter(d => (d.volatilityScore || 0) >= 70).length;
  const bull    = filtered.filter(d => (d.probUp          || 0) >= 55).length;

  return (
    <div>
      <CacheBar cacheTs={cacheTs} cacheStale={stale} weekRange={data?.weekRange} onSync={() => sync(true)} syncing={syncing} label="Earnings" shared={sharedCache} />
      {error && <ErrMsg msg={error} onRetry={sync} />}

      {/* Debug panel — Yahoo Finance status */}
      {data?.debug && (
        <details style={{ marginBottom: 10 }}>
          <summary style={{ fontSize: 10, color: 'var(--muted)', cursor: 'pointer', letterSpacing: '.06em' }}>
            ◎ Yahoo Finance debug · source: <span style={{ color: data.yahooSource === 'none' ? 'var(--down)' : 'var(--up)' }}>{data.yahooSource || 'none'}</span> · {data.tickers?.length || 0} tickers
          </summary>
          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', marginTop: 6, fontFamily: 'monospace', fontSize: 10, color: 'var(--muted)', maxHeight: 160, overflowY: 'auto' }}>
            {(data.debug || []).map((line, i) => <div key={i}>{line}</div>)}
          </div>
        </details>
      )}

      {/* Sort + sector filter controls */}
      {data && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 9, color: 'var(--muted)', letterSpacing: '.1em', textTransform: 'uppercase' }}>Sort</span>
          {[
            ['volatilityScore', 'Volatility'],
            ['impliedMove',     'Impl. move'],
            ['ivRank',          'IV rank'],
            ['probUp',          'Bull prob'],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setSortBy(k)} style={{
              background: sortBy === k ? 'var(--accent)' : 'var(--bg-hover)',
              color: sortBy === k ? 'var(--bg)' : 'var(--muted)',
              border: '1px solid var(--border)', padding: '3px 9px', borderRadius: 4,
              fontSize: 10, fontWeight: sortBy === k ? 700 : 400, cursor: 'pointer',
            }}>{l}</button>
          ))}
          <select
            value={sector} onChange={e => setSector(e.target.value)}
            style={{ marginLeft: 'auto', background: 'var(--bg-elev)', color: 'var(--text)', border: '1px solid var(--border)', padding: '3px 8px', borderRadius: 4, fontSize: 11 }}
          >
            {sectors.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
      )}

      {/* Summary stat bar */}
      {filtered.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 14 }}>
          {[
            ['Avg implied move', `±${fmt(avgImpl)}%`,          'var(--warn)'],
            ['Avg IV rank',      fmt(avgIV, 0),                avgIV >= 60 ? 'var(--down)' : avgIV >= 35 ? 'var(--warn)' : 'var(--up)'],
            ['High vol picks',   `${hiVol}/${filtered.length}`, 'var(--down)'],
            ['Bullish bias',     `${bull}/${filtered.length}`,  'var(--up)'],
          ].map(([lbl, val, color]) => (
            <div key={lbl} style={{ background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 6, padding: '10px 12px' }}>
              <div style={{ fontSize: 9, color: 'var(--muted)', letterSpacing: '.1em', textTransform: 'uppercase' }}>{lbl}</div>
              <div style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 700, color, marginTop: 2 }}>{val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Market context blurb from AI */}
      {data?.marketContext && (
        <div style={{ background: 'var(--accent-soft)', border: '1px solid var(--border)', borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
          <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase' }}>Market context · </span>
          <span style={{ fontSize: 13, color: 'var(--text)' }}>{data.marketContext}</span>
        </div>
      )}

      {!data && !syncing && <EmptyState label="Earnings" onSync={sync} syncing={syncing} />}
      {syncing && <Spinner />}

      {/* Split into this week / next week */}
      {(() => {
        const thisWeekEnd   = data?.thisWeekEnd   || '';
        const nextWeekStart = data?.nextWeekStart || '';
        const thisWeek = filtered.filter(s => !s.reportDate || s.reportDate <= thisWeekEnd);
        const nextWeek = filtered.filter(s => s.reportDate && s.reportDate >= nextWeekStart);
        const renderSection = (label, items, startIdx) => items.length === 0 ? null : (
          <div key={label}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--muted)', margin: '14px 0 8px', paddingBottom: 4, borderBottom: '1px solid var(--border)' }}>
              {label}
            </div>
            {items.map((s, i) => (
              <EarningsCard
                key={s.ticker} stock={s} rank={startIdx + i} t={t}
                expanded={expanded === startIdx + i}
                onToggle={() => setExpanded(expanded === startIdx + i ? -1 : startIdx + i)}
              />
            ))}
          </div>
        );
        return (
          <>
            {renderSection('This Week', thisWeek, 0)}
            {renderSection('Next Week', nextWeek, thisWeek.length)}
          </>
        );
      })()}

      {data && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--faint)', fontSize: 12 }}>
          No stocks match this filter.
        </div>
      )}
    </div>
  );
}
